import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import pluralize from 'pluralize';
import moment from 'moment';
import EmployeesLimitReachedAction from '../../components/manager/employees-limit-reached-action';
import {
    Block,
    FlexDiv,
    palette,
    Tag,
    StatusBadge,
    TextDefault,
} from '@awarego-ui/components';
import StyledLink from '../../components/StyledLink';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import CreateEditListModal from './modals/CreateEditListModal';
import UsersAddModal from './modals/UsersAddModal';
import DynamicListCreateModal from './modals/DynamicListCreateModal';
import TableClient from '../../components/table/TableClient';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderDetailsList from '../../components/header/SubHeaderDetailsList';
import { ThemedButton } from '@awarego-ui/components';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { formatDate } from '../../utils/helpers';
import { caseAndDiacriticsInsensitiveSort } from '../../components/table/table-helpers';
import { LINKS } from '../../utils/constants';
import { createNoUsersView, createBadge, tagColumnHoldersColumn } from './users-helpers';
import { FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import orderBy from 'lodash/orderBy';

function parseListMetadata(raw) {
    if (raw == null) return {};
    if (typeof raw === 'object') return { ...raw };
    try {
        const o = JSON.parse(raw);
        return o && typeof o === 'object' ? { ...o } : {};
    } catch (e) {
        return {};
    }
}

function UsersListsTable({
    companyId,
    heading,
    createDisabled,
    currentCompany,
}) {
    const { userStore, listStore, brandingStore, store } = useStores();
    const [searchValue, setSearchValue] = useState('');
    const [groupByOrganization, setGroupByOrganization] = useState(true);
    const [isCreateModalOpen, setCreateModalIsOpen] = useState(false);
    const [isImportModalOpen, setImportModalIsOpen] = useState(false);
    const [isDynamicListModalOpen, setDynamicListModalOpen] = useState(false);
    const [error, setError] = useState(null);
    const [loadingState, setLoadingState] = useState(true);
    const [filteredListsCount, setFilteredListsCount] = useState(null);
    const rowsPerPage = 10;

    const loadData = useCallback(async () => {
        setLoadingState(true);
        await listStore.loadCompanyLists(companyId);
        setLoadingState(false);
    }, [companyId, listStore]);

    useEffect(() => {
        loadData();
    }, [loadData, userStore.usersDeleted, userStore.usersAdded]);

    const dynamicListsEnabled = brandingStore.dynamicListsEnabled;

    const organizeByOrganizationEnabled =
        store.currentCompanyUISettings[
            'ui.organize_employee_lists_by_organization'
        ] === '1';
    const tagColumn = String(
        store.currentCompanyUISettings['ui.Tag_column'] || ''
    ).trim();

    useEffect(() => {
        if (organizeByOrganizationEnabled) {
            setGroupByOrganization(true);
        }
    }, [organizeByOrganizationEnabled]);

    const listsByExternalId = useMemo(() => {
        const m = {};
        (listStore.lists || []).forEach((l) => {
            if (l.external_id) m[l.external_id] = l;
        });
        return m;
    }, [listStore.lists]);

    const resolveOrganizationLabel = useCallback(
        (list) => {
            const meta = parseListMetadata(list.metadata);
            const oid = meta.organization;
            if (!oid) return '';
            if (meta.organizationName) {
                return String(meta.organizationName);
            }
            const parent = listsByExternalId[oid];
            return (parent && parent.name) || String(oid);
        },
        [listsByExternalId]
    );

    /** When grouping is on, sort in JS (org blocks then name); react-table cannot keep stable org blocks with column desc toggles.
     * Do not wrap in useMemo with only `listStore.lists` as dep: MobX keeps the same array reference after `.replace()`, so React
     * would keep a stale memo (e.g. empty []) until another dep like `groupByOrganization` changes.
     */
    const rows = (listStore.lists || []).slice();
    const tableData =
        !organizeByOrganizationEnabled || !groupByOrganization
            ? rows
            : orderBy(
                  rows,
                  [
                      (l) => {
                          const m = parseListMetadata(l.metadata);
                          return m.organization ? 0 : 1;
                      },
                      (l) =>
                          (resolveOrganizationLabel(l) || '').toLowerCase(),
                      (l) =>
                          String(
                              parseListMetadata(l.metadata).organization ||
                                  ''
                          ),
                      (l) => (l.name || '').toLowerCase(),
                  ],
                  ['asc', 'asc', 'asc', 'asc']
              );

    const disableColumnSortWhileGrouped =
        organizeByOrganizationEnabled && groupByOrganization;

    const buttons = useMemo(
        () => (
            <FlexDiv gap={16}>
                <ThemedButton
                    mode="secondary"
                    onClick={() => {
                        setCreateModalIsOpen(true);
                    }}
                    text="Create new"
                />
                {dynamicListsEnabled && (
                    <ThemedButton
                        mode="secondary"
                        onClick={() => {
                            setDynamicListModalOpen(true);
                        }}
                        disabled={createDisabled}
                        text={<AutoAwesomeIcon />}
                        title="Create dynamic list"
                    />
                )}
                <ThemedButton
                    mode="secondary"
                    text="Import"
                    onClick={() => {
                        setImportModalIsOpen(true);
                    }}
                    disabled={createDisabled}
                />
                <ThemedButton
                    mode="secondary"
                    disabled={userStore.isDownloading}
                    onClick={() => userStore.downloadCSV(companyId)}
                    text={
                        <>
                            {userStore.isDownloading && (
                                <CircularProgress size={14} />
                            )}{' '}
                            Export
                        </>
                    }
                />
            </FlexDiv>
        ),
        [createDisabled, dynamicListsEnabled, userStore.isDownloading, companyId]
    );

    const createDetailsString = useCallback(() => {
        let details = [];
        let filteredLists =
            filteredListsCount == null
                ? listStore.lists.length
                : filteredListsCount;
        let totalLists = listStore.lists ? listStore.lists.length : 0;

        if (filteredLists !== totalLists) {
            details.push(`${filteredLists}/${totalLists}`);
        } else {
            details.push(totalLists);
        }
        details.push(pluralize('list', listStore.lists.length));

        return details.join(' ');
    }, [listStore.lists, userStore.usersTotal, filteredListsCount]);

    const columns = useMemo(
        () => {
            const nameSortType = disableColumnSortWhileGrouped
                ? caseAndDiacriticsInsensitiveSort
                : (rowA, rowB, columnId, desc) => {
                      const inner = caseAndDiacriticsInsensitiveSort(
                          rowA,
                          rowB,
                          columnId
                      );
                      return desc ? -inner : inner;
                  };
            const cols = [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: (x) => {
                    const list = x.row.original;
                    const isBatchSync = list.source === 'batch-sync';
                    const syncedDate = isBatchSync && list.updated ? moment(list.updated).fromNow() : null;
                    return (
                        <FlexDiv gap={8} alignCenter>
                            <StyledLink
                                to={`/users/list/${x.row.original.id}`}
                                $hoverColor={palette.graphite.onyx}
                                $underlined={true}
                            >
                                {x.value && x.value.length > 0 ? (
                                    x.value
                                ) : (
                                    <Tag text="No Name" />
                                )}{' '}
                            </StyledLink>
                            {createBadge(list)}
                            {isBatchSync && syncedDate && (
                                <StatusBadge
                                    color={palette.evergreen.aspargus}
                                    text={`Synced ${syncedDate}`}
                                />
                            )}
                        </FlexDiv>
                    );
                },
                sortType: nameSortType,
            },
            {
                Header: 'Created on',
                accessor: 'created',
                Cell: (x) => formatDate(x.value),
                minWidth: 144,
                sortType: disableColumnSortWhileGrouped
                    ? (rowA, rowB, columnId) => {
                          const ta = new Date(rowA.values[columnId]).getTime();
                          const tb = new Date(rowB.values[columnId]).getTime();
                          if (ta < tb) return -1;
                          if (ta > tb) return 1;
                          return 0;
                      }
                    : (rowA, rowB, columnId, desc) => {
                          const ta = new Date(
                              rowA.values[columnId]
                          ).getTime();
                          const tb = new Date(
                              rowB.values[columnId]
                          ).getTime();
                          if (ta < tb) return desc ? 1 : -1;
                          if (ta > tb) return desc ? -1 : 1;
                          return 0;
                      },
            },
            {
                Header: 'Employees',
                accessor: 'usersCount',
                sortType: disableColumnSortWhileGrouped
                    ? (rowA, rowB, columnId) => {
                          const va = rowA.values[columnId] ?? 0;
                          const vb = rowB.values[columnId] ?? 0;
                          if (va < vb) return -1;
                          if (va > vb) return 1;
                          return 0;
                      }
                    : (rowA, rowB, columnId, desc) => {
                          const va = rowA.values[columnId] ?? 0;
                          const vb = rowB.values[columnId] ?? 0;
                          if (va < vb) return desc ? 1 : -1;
                          if (va > vb) return desc ? -1 : 1;
                          return 0;
                      },
            },
            ];
            if (organizeByOrganizationEnabled) {
                cols.splice(1, 0, {
                    Header: 'Organization',
                    id: 'organization',
                    accessor: (row) => resolveOrganizationLabel(row),
                    Cell: ({ value }) => value || '—',
                    sortType: disableColumnSortWhileGrouped
                        ? caseAndDiacriticsInsensitiveSort
                        : (rowA, rowB, columnId, desc) => {
                              const inner = caseAndDiacriticsInsensitiveSort(
                                  rowA,
                                  rowB,
                                  columnId
                              );
                              return desc ? -inner : inner;
                          },
                });
            }
            if (tagColumn) {
                const insertAt = organizeByOrganizationEnabled ? 2 : 1;
                const col = tagColumnHoldersColumn(tagColumn, {
                    sortType: disableColumnSortWhileGrouped
                        ? caseAndDiacriticsInsensitiveSort
                        : (rowA, rowB, columnId, desc) => {
                              const inner = caseAndDiacriticsInsensitiveSort(
                                  rowA,
                                  rowB,
                                  columnId
                              );
                              return desc ? -inner : inner;
                          },
                });
                if (col) cols.splice(insertAt, 0, col);
            }
            return cols;
        },
        [
            caseAndDiacriticsInsensitiveSort,
            organizeByOrganizationEnabled,
            disableColumnSortWhileGrouped,
            resolveOrganizationLabel,
            tagColumn,
        ]
    );

    return (
        <Block gap={24} tablePadding>
            {error && <ErrorMessage error={error} />}
            <HeaderSection
                heading={heading}
                searchValue={searchValue}
                searchPlaceholder={'Search lists'}
                onSearch={(e) => {
                    const q = (e.target.value || '').toLowerCase();
                    const filteredLists = listStore.lists.filter((l) => {
                        if (
                            l.name &&
                            l.name.toLowerCase().includes(q)
                        ) {
                            return true;
                        }
                        if (tagColumn) {
                            const holders = (l.tagColumnHolders || [])
                                .map(
                                    (h) =>
                                        `${h.name || ''} ${h.email || ''}`
                                )
                                .join(' ')
                                .toLowerCase();
                            if (holders.includes(q)) return true;
                        }
                        if (!organizeByOrganizationEnabled || !q) {
                            return false;
                        }
                        const orgLabel = resolveOrganizationLabel(l);
                        return (
                            orgLabel &&
                            orgLabel.toLowerCase().includes(q)
                        );
                    });
                    setFilteredListsCount(filteredLists.length);
                    setSearchValue(e.target.value || '');
                }}
                onClearSearch={() => {
                    setFilteredListsCount(listStore.lists.length);
                    setSearchValue('');
                }}
                buttons={buttons}
                subHeadings={
                    <FlexDiv gap={16} alignCenter wrap>
                        <SubHeaderDetailsList
                            key="details"
                            details={[createDetailsString()]}
                        />
                        {organizeByOrganizationEnabled && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={groupByOrganization}
                                        onChange={(ev) =>
                                            setGroupByOrganization(
                                                ev.target.checked
                                            )
                                        }
                                    />
                                }
                                label="Group by organization"
                            />
                        )}
                        {organizeByOrganizationEnabled &&
                            groupByOrganization && (
                                <TextDefault
                                    as="span"
                                    lighter
                                    style={{ fontSize: 12 }}
                                >
                                    Sorted by organization, then name
                                    (A–Z). Turn off to use column headers.
                                </TextDefault>
                            )}
                    </FlexDiv>
                }
                excludeBlock
                marginLeft="16"
            />
            <EmployeesLimitReachedAction
                disabled={createDisabled}
                currentCompany={currentCompany}
            />

            <CreateEditListModal
                isModalOpen={isCreateModalOpen}
                errorSetter={setError}
                companyId={companyId}
                heading="Create Employee list"
                onRequestClose={() => setCreateModalIsOpen(false)}
            ></CreateEditListModal>
            {dynamicListsEnabled && (
                <DynamicListCreateModal
                    isModalOpen={isDynamicListModalOpen}
                    companyId={companyId}
                    errorSetter={setError}
                    onRequestClose={() => setDynamicListModalOpen(false)}
                    onSuccess={() => {
                        setDynamicListModalOpen(false);
                        loadData();
                    }}
                ></DynamicListCreateModal>
            )}
            <UsersAddModal
                isModalOpen={isImportModalOpen}
                companyId={companyId}
                isImportingLists
                onRequestClose={() => setImportModalIsOpen(false)}
            ></UsersAddModal>

            <TableClient
                data={tableData}
                columns={columns}
                hidePagination={
                    listStore.lists && listStore.lists.length < rowsPerPage
                }
                defaultSortBy={'name'}
                defaultSortDescent={false}
                disableSorting={disableColumnSortWhileGrouped}
                link={LINKS.list}
                loadingData={listStore.loadingLists || loadingState}
                NoDataComponent={createNoUsersView(
                    currentCompany,
                    createDisabled,
                    'lists'
                )}
                filterValue={searchValue}
                initialPageSize={rowsPerPage}
            />
        </Block>
    );
}

export default observer(UsersListsTable);
